import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { Plus, Trash2, Save, Loader2, AlertCircle, X, ChevronDown, ChevronRight, Copy, RotateCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Toaster, toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PRICING_MODELS = [
  { value: 'standard', label: 'Standard (size-based)' },
  { value: 'fixed_price', label: 'Fixed price per location' },
  { value: 'stole', label: 'Graduation stole (base + per-side)' },
];

const COMPLEXITY_OPTIONS = ['text_only', 'logo_with_words', 'filled_logo_with_words'];

const formatErr = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(d);
};

const blankPlacement = () => ({
  product_type: '',
  label: '',
  decoration_methods: ['embroidery'],
  pricing_model: 'standard',
  locations: [],
  packages: [],
  base_options: [],
  extras: {},
  is_active: true,
  sort_order: 0,
});

const AdminPlacements = () => {
  const { token } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/placements`, { headers });
      setList(res.data.placements || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const startCreate = () => setEditing(blankPlacement());
  const startEdit = (p) => setEditing(JSON.parse(JSON.stringify(p))); // deep clone

  const save = async () => {
    if (!editing.product_type || !editing.label) {
      toast.error('product_type and label are required');
      return;
    }
    try {
      if (editing.id) {
        await axios.put(`${API}/api/admin/placements/${editing.id}`, editing, { headers });
        toast.success('Saved');
      } else {
        await axios.post(`${API}/api/admin/placements`, editing, { headers });
        toast.success('Created');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete placement "${p.label}" (${p.product_type})? Existing quotes are unaffected.`)) return;
    try {
      await axios.delete(`${API}/api/admin/placements/${p.id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const reseedDefaults = async (overwrite) => {
    const confirmMsg = overwrite
      ? 'Overwrite locations + methods on EVERY product type with the canonical CSV defaults? Your custom edits to those types will be lost. Existing custom product types (not in the CSV) are kept.'
      : 'Fill in any missing canonical product types from the CSV defaults? Existing product types are left untouched.';
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await axios.post(
        `${API}/api/admin/placements/reseed-defaults?overwrite=${overwrite ? 'true' : 'false'}`,
        {},
        { headers }
      );
      toast.success(`Touched ${r.data.touched} product types`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div className="w-full min-w-0 px-4 py-6" data-testid="admin-placements-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1B3A5C]">Decoration Placements</h1>
            <p className="text-sm text-gray-500 mt-1">
              Per-garment placement library — what locations / sizes / complexities the Quote Builder offers for each product family.
            </p>
          </div>
          <button onClick={startCreate}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F5A623] text-[#1B3A5C] font-bold text-sm rounded-full hover:bg-[#D88F1B]"
            data-testid="add-placement-btn">
            <Plus className="w-4 h-4" /> Add Product Type
          </button>
          <button onClick={() => reseedDefaults(false)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-gray-700 font-medium text-xs rounded-full hover:bg-gray-50"
            data-testid="fill-missing-defaults-btn"
            title="Insert any canonical product types from the CSV that aren't here yet. Existing rows untouched.">
            <Plus className="w-3.5 h-3.5" /> Fill missing defaults
          </button>
          <button onClick={() => reseedDefaults(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-200 text-red-600 font-medium text-xs rounded-full hover:bg-red-50"
            data-testid="overwrite-defaults-btn"
            title="Replace locations/methods on every canonical product type with the CSV defaults. Destructive.">
            <RotateCw className="w-3.5 h-3.5" /> Reset to CSV
          </button>
        </div>

        {err && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
            <button onClick={() => setErr('')} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : (
          <div className="space-y-2">
            {list.map((p) => (
              <PlacementRow key={p.id} p={p} onEdit={() => startEdit(p)} onDelete={() => remove(p)} />
            ))}
            {list.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-12">No placement configs yet. Click "Add Product Type" to create the first one.</p>
            )}
          </div>
        )}
      </div>

      {editing && (
        <PlacementFormModal
          value={editing}
          onChange={setEditing}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </AdminLayout>
  );
};

const PlacementRow = ({ p, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid={`placement-row-${p.product_type}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-gray-50/40">
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <div>
            <p className="font-semibold text-[#1B3A5C]">{p.label}</p>
            <p className="text-xs text-gray-400 font-mono">{p.product_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {p.decoration_methods.map((m) => (
            <span key={m} className="px-2 py-0.5 rounded-full bg-[#F5A623]/10 text-[#F5A623] text-[10px] font-bold uppercase">{m}</span>
          ))}
          {p.pricing_model !== 'standard' && (
            <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold uppercase">{p.pricing_model}</span>
          )}
          <span className="text-xs text-gray-500">{p.locations.length} loc{p.locations.length === 1 ? '' : 's'}</span>
          {!p.is_active && <span className="text-[10px] font-bold uppercase text-gray-400">disabled</span>}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-4 border-t border-gray-100 pt-4">
          <div className="grid sm:grid-cols-2 gap-2 mb-4">
            {p.locations.map((loc) => (
              <div key={loc.id} className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-100 text-sm">
                <p className="font-medium text-gray-800">{loc.label}</p>
                <p className="text-xs text-gray-500">
                  {loc.size_range ? `${loc.size_range}" • ` : (loc.sizes?.length ? `${loc.sizes.join('"/')}\" • ` : '')}
                  {loc.methods?.join(', ')}
                  {loc.fixed_price ? ` • $${loc.fixed_price} fixed` : ''}
                </p>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={onEdit} className="px-4 py-2 text-sm rounded-lg bg-[#1B3A5C] text-white hover:bg-[#0F2A3D]" data-testid={`edit-placement-${p.product_type}`}>Edit</button>
            <button onClick={onDelete} className="px-4 py-2 text-sm rounded-lg border border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const PlacementFormModal = ({ value, onChange, onSave, onClose }) => {
  const update = (patch) => onChange({ ...value, ...patch });
  const toggleMethod = (m) => {
    const has = value.decoration_methods.includes(m);
    update({ decoration_methods: has ? value.decoration_methods.filter((x) => x !== m) : [...value.decoration_methods, m] });
  };

  const updateLoc = (i, patch) => {
    const next = [...value.locations];
    next[i] = { ...next[i], ...patch };
    update({ locations: next });
  };
  const addLoc = () => update({
    locations: [...value.locations, { id: '', label: '', sizes: [], complexities: [], methods: value.decoration_methods.slice(), fixed_price: null }],
  });
  const removeLoc = (i) => update({ locations: value.locations.filter((_, idx) => idx !== i) });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[70]" data-testid="placement-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-bold text-[#1B3A5C]">{value.id ? `Edit "${value.label}"` : 'Add Product Type'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Product type slug *</label>
              <input value={value.product_type} disabled={!!value.id} onChange={(e) => update({ product_type: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_') })}
                placeholder="tee, hoodie, polo, ..."
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30 text-sm font-mono disabled:bg-gray-50 disabled:text-gray-400"
                data-testid="placement-product-type" />
              <p className="text-xs text-gray-400 mt-1">Lowercase letters/numbers/underscores. Cannot change once saved.</p>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Display label *</label>
              <input value={value.label} onChange={(e) => update({ label: e.target.value })}
                placeholder="T-Shirt"
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30 text-sm"
                data-testid="placement-label" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Decoration methods</label>
            <div className="flex gap-2">
              {['embroidery', 'printing'].map((m) => (
                <button key={m} type="button" onClick={() => toggleMethod(m)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${value.decoration_methods.includes(m) ? 'bg-[#F5A623] text-[#1B3A5C] border-[#F5A623]' : 'bg-white text-gray-600 border-gray-200'}`}
                  data-testid={`placement-method-${m}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Pricing model</label>
              <select value={value.pricing_model} onChange={(e) => update({ pricing_model: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/30 text-sm">
                {PRICING_MODELS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={!!value.is_active} onChange={(e) => update({ is_active: e.target.checked })} className="accent-[#F5A623]" data-testid="placement-active" />
                <span>Active (used by Quote Builder)</span>
              </label>
            </div>
          </div>

          {/* Locations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">Locations</label>
              <button type="button" onClick={addLoc} className="text-xs text-[#F5A623] font-bold hover:text-[#D88F1B] inline-flex items-center gap-1" data-testid="add-location-btn">
                <Plus className="w-3 h-3" /> Add Location
              </button>
            </div>
            <div className="space-y-3">
              {value.locations.map((loc, i) => (
                <div key={i} className="p-3 rounded-xl border border-gray-200 bg-gray-50/60 space-y-2" data-testid={`location-row-${i}`}>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input value={loc.id || ''} onChange={(e) => updateLoc(i, { id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_') })}
                      placeholder="left_chest" className="px-3 py-2 rounded-lg border border-gray-200 text-sm font-mono" />
                    <input value={loc.label || ''} onChange={(e) => updateLoc(i, { label: e.target.value })}
                      placeholder="Left Chest" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                  </div>
                  <div className="grid sm:grid-cols-4 gap-2">
                    <input value={loc.size_range || ''} onChange={(e) => updateLoc(i, { size_range: e.target.value })}
                      placeholder="Size range (e.g. 4-6)" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" title="Inches" />
                    <input value={loc.default_size ?? ''} onChange={(e) => updateLoc(i, { default_size: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      placeholder="Default size" type="number" min="0" step="0.25" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                    <input value={loc.fixed_price ?? ''} onChange={(e) => updateLoc(i, { fixed_price: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      placeholder="Fixed price (if any)" type="number" min="0" step="0.01" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" />
                    <div className="flex items-center gap-2">
                      {['embroidery', 'printing'].map((m) => (
                        <label key={m} className="flex items-center gap-1 text-xs cursor-pointer">
                          <input type="checkbox" checked={(loc.methods || []).includes(m)}
                            onChange={(e) => updateLoc(i, { methods: e.target.checked ? [...(loc.methods || []), m] : (loc.methods || []).filter((x) => x !== m) })}
                            className="accent-[#F5A623]" />
                          {m}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-gray-500">Complexities:</span>
                    {COMPLEXITY_OPTIONS.map((c) => (
                      <label key={c} className="flex items-center gap-1 text-xs cursor-pointer">
                        <input type="checkbox" checked={(loc.complexities || []).includes(c)}
                          onChange={(e) => updateLoc(i, { complexities: e.target.checked ? [...(loc.complexities || []), c] : (loc.complexities || []).filter((x) => x !== c) })}
                          className="accent-[#F5A623]" />
                        {c.replace(/_/g, ' ')}
                      </label>
                    ))}
                    <button type="button" onClick={() => removeLoc(i)} className="ml-auto text-xs text-red-500 hover:text-red-700 inline-flex items-center gap-1">
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              ))}
              {value.locations.length === 0 && (
                <p className="text-xs text-gray-400 text-center py-6 border border-dashed border-gray-200 rounded-xl">No locations yet. Click "Add Location" to add one.</p>
              )}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={onSave} className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#F5A623] text-[#1B3A5C] font-bold text-sm hover:bg-[#D88F1B]" data-testid="save-placement-btn">
            <Save className="w-4 h-4" /> Save Placement
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPlacements;
